import {DateTime} from "luxon";
import brandHelper from "app/shared/utils/BrandHelper";
import logging from "app/shared/utils/logging";
import { ASAP, DateFormat } from "app/shared/constants/constants";
import { getLabel } from "app/shared/constants/Literals";
import { DataLayerItem } from "app/types/DataLayerType";
import { PICKUP_TYPES, PickupType } from "app/shared/constants/PickupTypes";

export const US_STATES: string[] = ["AL", "AK", "AS", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FM", "FL", "GA", "GU", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MH", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "MP", "OH", "OK", "OR", "PW", "PA", "PR", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VI", "VA", "WA", "WV", "WI", "WY", "Armed Forces (AA)", "Armed Forces (AE)", "ArmedForces (AP)"].sort();

export const redirectToBrandSite = () => {
    const url = brandHelper()?.deepLink;
    if (url) {
        window.location.href = url;
    }
};

export const convertTime = (inputTime: string, timezone: string): string => {
  try {
    if (timezone === '') {
      return inputTime;
    }
    if (inputTime?.includes('ASAP')) {
      return `${DateTime?.local()?.setZone(timezone)?.toLocaleString()}, ${inputTime}`;
    }
    const [hoursPart, ampmPart] = inputTime.split(' ');
    const [hours, minutes] = hoursPart.split(':').map((part) => parseInt(part.trim(), 10));
    const date = new Date();
    if (ampmPart === 'PM' && hours < 12) {
      date.setHours(hours + 12);
    } else {
      date.setHours(hours);
    }
    date.setMinutes(minutes);
    const formattedDatetime = date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
    return `${formattedDatetime}`;
  } catch (e) {
    logging.debug(
        "_Confirmation_Date_Conversion",
        "Failed while converting the selected time to date from checkout",
        e,
    );
    return inputTime;
  }
};

export const convertDate = (inputTime: string, timezone: string): string => {
  try {
    if (timezone === '') {
      return inputTime;
    }
    if (inputTime?.includes('ASAP')) {
      return `${DateTime?.local()?.setZone(timezone)?.plus({ minutes: 20 })?.toFormat('h.mm a M/d/yyyy')}`;
    }
    const [hoursPart, ampmPart] = inputTime.split(' ');
    const [hours, minutes] = hoursPart.split(':').map((part) => parseInt(part.trim(), 10));
    const date = new Date();
    if (ampmPart === 'PM' && hours < 12) {
      date.setHours(hours + 12);
    } else {
      date.setHours(hours);
    }
    date.setMinutes(minutes);
    return DateTime.fromJSDate(date)?.toFormat('h.mm a M/d/yyyy');
  } catch (e) {
    logging.debug(
        "_Confirmation_Date_Conversion",
        "Failed while converting the selected time to date from checkout",
        e,
    );
    return inputTime;
  }
};

export const isNotEmpty = (value: number | string | undefined): boolean => {
    if (typeof value === 'string' && value.trim() === '') {
        return false;
    }
    if (value === null || value === undefined) {
        return false;
    }
    return true;
};

export const convertDateToUTC = (date: string, timezone: string) => {
  let dateString = "";
  if (date === 'now') {
      //convert the current(ASAP) time to UTC format
      dateString = DateTime.local()?.setZone(timezone)?.toUTC()?.toString();
  } else {
      //convert the selected future time to ISO format first
      const dateToISOFormat = DateTime.fromFormat(date, "yyyy-MM-dd HH:mm:ss", { zone: timezone })?.toString();
      //convert from ISO to UTC format
      dateString = DateTime.fromISO(dateToISOFormat, {zone: 'utc'})?.toString();
  }
  return dateString;
};

/**
 * convertDateTimetoISO
 * Converts a date string to an ISO  format string based on either a timezone or a fixed offset.
 * @param {Object} params - The parameters for conversion.
 * @param {string} params.date - The input date string to convert.
 * @param {string} [params.offset] - The fixed offset e.g. "+02:00"
 * @param {string} [params.timezone] - The IANA timezone name e.g. "America/New_York"
 * @returns {string} The date converted to ISO format.
 */
export const convertDateTimetoISO = ({
  date,
  offset,
  timezone,
}: (
  | { date: string; offset: string; timezone?: never }
  | { date: string; timezone: string; offset?: never }
)): string => {
  const dateString = date.replace('UTC', '').trim();
  let offsetStr = offset || "";
  if (!offsetStr && timezone) {
    offsetStr = DateTime.now().setZone(timezone).toFormat("ZZ");
  }

  const zone = timezone || `UTC${offsetStr}`;
  return DateTime.fromFormat(dateString, "yyyy-MM-dd HH:mm:ss", { zone })?.toString();
};

/**
 * ADA Compliance Props for Buttons
 */
export const adaButtonProps = {
  onKeyDown: (event: React.KeyboardEvent<unknown>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.target.dispatchEvent(new Event("click", { bubbles: true }));
    }
  },
  role: "button",
  tabIndex: 0,
};

export const formatCurrency = (amount: number, locale = 'en-US', currency = 'USD') => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
/**
 * convertPickupDateTime
 * This function converts the date and time from ISO to MM/dd/yyyy h:mm a
 * If isFutureOrdersEnabled retuns only the converted time
 * @param {Object} params - The parameters for conversion.
 * @param {string} params.pickUpTime - The input time string to convert.
 * @param {string} params.pickUpDate - The input date string to convert.
 * @param {string} params.timezone - The store timezone
 * @param {boolean} params.isFutureOrdersEnabled - Featureflag value.
 * @returns {string} result of date convertion MM/dd/yyyy h:mm a
 */
export const convertPickupDateTime = ({
  pickUpTime,
  isFutureOrdersEnabled = false,
  pickUpDate,
  timezone,
}: {
  pickUpTime: string,
  isFutureOrdersEnabled?: boolean,
  pickUpDate: string,
  timezone: string
}) => {
    if (!isFutureOrdersEnabled || pickUpTime.includes(ASAP)) {
      return convertTime(pickUpTime, timezone);
    }

    const formattedDate = pickUpDate.replaceAll('UTC', '').trim();
    const parsed = DateTime.fromFormat(
      formattedDate,
      DateFormat.yyyy_MM_dd_HH_mm_ss,
      { zone: timezone },
    );

    return parsed.isValid
      ? parsed.toFormat(DateFormat.MM_dd_yyyy_h_mm_a)
      : convertTime(pickUpTime, timezone);
  };

/**
 * isTodayDate
 * This function compares an input date with the current date.
 * If the input date is the same (ignoring time), it returns true; otherwise, it returns false.
 * @param {string} inputDate - Date to compare
 * @param {string} zone - store timezone
 * @returns {boolean} result of the comparison
 */
export const isTodayDate = (inputDate: string, zone: string): boolean => {
  const dateString = inputDate.replace('UTC', '').trim();
  const dateTimeString = dateString.length > 10 ? dateString : dateString.concat(' 00:00:00');
  const input = DateTime.fromFormat(dateTimeString, "yyyy-MM-dd HH:mm:ss", { zone });
  const now = DateTime.utc().setZone(zone, { keepLocalTime: false });
  return input?.hasSame(now, 'day') || false;
};

export const formatDateLabel = (dateString: string, formatToUpperCase = true): string => {
  const date = DateTime.fromISO(dateString).startOf("day");
  const today = DateTime.now().startOf("day");
  const tomorrow = today.plus({ days: 1 });

  let label = date.toFormat("MMM d, yyyy");

  if (date.equals(today)) label = getLabel("FUTURE_ORDERS", "today");
  if (date.equals(tomorrow)) label = getLabel("FUTURE_ORDERS", "tomorrow");

  return formatToUpperCase ? label.toUpperCase() : label;
};

export const formatTimeLabel = (timeString: string): string => {
  if (!timeString) return "";
  if (timeString.includes("ASAP")) return timeString;

  const time = DateTime.fromFormat(timeString, "HH:mm");
  return time.toFormat("h:mm a");
};

 export const sendToDataLayer = (dataLayerItem: DataLayerItem): void => {
    // Initialize dataLayer if it doesn't exist
    if (!window.dataLayer) {
        window.dataLayer = [];
    }
    // Check if event already exists for this transaction
    const existingDataLayerItemIndex = window.dataLayer.findIndex(
        (item: DataLayerItem) => item.event === dataLayerItem.event,
    );
    if (existingDataLayerItemIndex !== -1) {
      window.dataLayer.splice(existingDataLayerItemIndex, 1); // Remove old purchase event
    }
    window.dataLayer.push(dataLayerItem);
};

/**
 * Returns the value of a given feature flag by brand
 * eg: getFeatureFlagValueByBrand("{"EBBAPP": "ebb title",
 * "NOAHSAPP": "noahs title", "BRUAPP": "bru title" }") ->
 * if brand is EBBAPP then it'll return "ebb title".
 *
 * @param {string} featureFlag - The key of the feature flag to retrieve.
 * @return {string} The value of the specified feature flag by brand.
 */
export const getFeatureFlagValueByBrand = (featureFlag: string): string => {
    const { appSource } = brandHelper();
    try {
      if (typeof featureFlag === "string" && featureFlag.trim()) {
        // Parse into a string-indexed record to avoid implicit any
        const parsedfeatureFlagConfig = JSON.parse(featureFlag) as Record<string, unknown>;
        const appSourceInUpperCase = appSource?.toUpperCase?.() ?? "";
        const value = appSourceInUpperCase ? parsedfeatureFlagConfig[appSourceInUpperCase]
        : undefined;
        return typeof value === "string" ? value : "";
      }
    } catch (error) {
      // Return empty string if parsing fails
      return "";
    }
    return "";
};

export function isIOSDevice() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false; // Not in a browser environment
  }
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function getPickUpDescription(pickUpType: string): string {
  return PICKUP_TYPES[pickUpType as keyof typeof PICKUP_TYPES]?.description || '';
}

export const getEnabledPickupTypeLabels = (
  rawPickupTypes: string[] = [],
): string[] => rawPickupTypes.flatMap((type) => {
  const normalizedType = type.toUpperCase() as PickupType;
  const config = PICKUP_TYPES[normalizedType];
  return config?.enabled ? [config.label] : [];
});

export function convertUTCDateToIso(date:string, zone: string): string {
  return DateTime.fromFormat(
          date,
          "yyyy-MM-dd HH:mm:ss 'UTC'",
          {zone},
          ).toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss.SSSZZZ");
}
